import React, { useContext } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import CustomDialog, { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import CustomAlert from "../../../components/Alert/CustomAlert";
import CustomTextField from "../../../components/Input/CustomTextField";
import { tesaBlue, tesaDarkRedError } from "../../../styles/colors";
import ProductApi from "../../../api/services/ProductApi";
import { Laboratory } from "../../../api/models/product/Laboratory";
import useEnv from "../../../hooks/env";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    invalidProdName: {
      color: tesaDarkRedError,
    },
    link: {
      cursor: "pointer",
      color: tesaBlue,
    },
    marginBottom: {
      marginBottom: theme.spacing(4),
    },
    marginTop: {
      marginTop: theme.spacing(5),
    },
    marginRight: {
      marginRight: theme.spacing(3),
    },
  }),
);

const LaboratoryCreateDialog = (props: CustomDialogProps) => {
  const classes = useStyles();
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const { open, onClose } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);

  const yupValidationSchema = Yup.object().shape({
    laboratoryName: Yup.string().required("Please enter a name"),
  });

  return (
    <CustomDialog fullWidth maxWidth="sm" onClose={() => onClose(false, "laboratory")} open={open}>
      <h2>CREATE TESA LABORATORY</h2>
      <CustomAlert
        className={classes.marginBottom}
        text="You are about to create a laboratory in the database for all users. Make sure that the laboratory is not already created and that the name is accurate."
      />
      <Formik
        initialValues={{
          laboratoryName: "",
        }}
        onSubmit={(values, { setSubmitting, setFieldError }) => {
          productApi
            .createNewLaboratory({ name: values.laboratoryName })
            .then(() => {
              showSnackbar("Laboratory created", "success");
              onClose(true, "laboratory");
            })
            .catch((e) => {
              handleHttpError(e);
              setSubmitting(false);
              setFieldError("laboratoryName", "This laboratory already exists.");
            });
        }}
        validationSchema={yupValidationSchema}
      >
        {(formikProps): JSX.Element => {
          // formik inner jsx form
          const { errors, handleBlur, handleChange, isSubmitting, touched, values } = formikProps;
          return (
            <Form>
              <Grid container justify="flex-start" direction="row" spacing={4}>
                <Grid item xs={12}>
                  <CustomTextField
                    autoFocus
                    fullWidth
                    mandatory
                    name="laboratoryName"
                    id="laboratoryName"
                    label="Laboratory Name"
                    value={values.laboratoryName}
                    type="text"
                    helperText={
                      errors.laboratoryName && touched.laboratoryName
                        ? errors.laboratoryName
                        : "Name of the Laboratory incl. BU and region"
                    }
                    error={!!(errors.laboratoryName && touched.laboratoryName)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>
              <Grid className={classes.marginTop} container direction="row" justify="flex-end">
                <CustomButton
                  disabled={isSubmitting}
                  text="Cancel"
                  className={classes.marginRight}
                  onClick={() => onClose(false, "laboratory")}
                />
                <CustomButton disabled={isSubmitting} primary text="Create" type="submit" />
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
};

export default LaboratoryCreateDialog;
